import { WEBSITE_REGEX } from "../../../utils/validationPatterns";
import { WEB_ADDRESS_FORMAT_MESSAGE } from "../../../utils/fieldMessages";
import {
  CmpProspectFieldsFragment,
  CmpProspectFieldsInput as GeneratedCmpProspectFieldsInput,
  GetUserQuery,
} from "../../../graphql/types";
import { getPreserveExpectedErrorsForCallerContext } from "../../../utils/apolloErrorPolicy";
import { getUserFacingErrorText } from "../../../utils/userFacingError";

export const PROSPECT_ERROR_CONTEXT = getPreserveExpectedErrorsForCallerContext(
  {
    expectedGraphQLErrorCodes: ["BAD_USER_INPUT"],
  },
);

type ProspectStaffDisplayNameInput = {
  user:
    | Pick<GetUserQuery["getUser"], "firstName" | "lastName" | "email">
    | null
    | undefined;
};

export const getProspectStaffDisplayName = ({
  user,
}: ProspectStaffDisplayNameInput): string => {
  // Match buildProspectStaffNote so the current and saved author labels agree.
  const displayName = [user?.firstName, user?.lastName]
    .map((name) => (name || "").trim())
    .filter(Boolean)
    .join(" ");

  return displayName || (user?.email || "").trim() || "Chamber Staff";
};

export type ProspectFormValues = {
  leadCaptureId: string;
  createdByName: string;
  companyName: string;
  companyWebsite: string;
  mainContactName: string;
  mainContactTitle: string;
  mainContactEmail: string;
  mainPhoneNumber: string;
  mainPhoneNumberExtension: string;
  mobilePhoneNumber: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  pendingNoteComment: string;
};

export type ProspectFormErrors = Partial<
  Record<keyof ProspectFormValues, string>
>;

export type ProspectFieldsInput = GeneratedCmpProspectFieldsInput;

export type ProspectRecordForForm = CmpProspectFieldsFragment;

export type ProspectSaveResult = CmpProspectFieldsFragment;

export const PROSPECT_FIELD_MAX_LENGTHS = {
  leadCaptureId: 200,
  createdByName: 100,
  companyName: 200,
  companyWebsite: 2048,
  mainContactName: 200,
  mainContactTitle: 100,
  mainContactEmail: 320,
  mainPhoneNumber: 32,
  mainPhoneNumberExtension: 8,
  mobilePhoneNumber: 32,
  streetAddress: 300,
  city: 100,
  province: 100,
  postalCode: 32,
  country: 100,
  leadCaptureLabel: 100,
  pendingNoteComment: 2000,
} as const;

export const emptyProspectFormValues: ProspectFormValues = {
  leadCaptureId: "",
  createdByName: "",
  companyName: "",
  companyWebsite: "",
  mainContactName: "",
  mainContactTitle: "",
  mainContactEmail: "",
  mainPhoneNumber: "",
  mainPhoneNumberExtension: "",
  mobilePhoneNumber: "",
  streetAddress: "",
  city: "",
  province: "",
  postalCode: "",
  country: "",
  pendingNoteComment: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_ALLOWED_CHARACTERS_PATTERN = /^[\d+().\s-]+$/;
const PHONE_EXTENSION_PATTERN = /^\d{1,8}$/;

/** Length error with the on-screen field label (MB-059). */
export const getProspectFieldLengthMessage = (label: string, maximumLength: number): string =>
  `${label} can have up to ${maximumLength} characters. Shorten it and try again.`;

export const validateProspectForm = (
  values: ProspectFormValues,
): ProspectFormErrors => {
  const errors: ProspectFormErrors = {};

  if (!values.leadCaptureId.trim()) {
    errors.leadCaptureId = "Lead Capture is required";
  }
  if (!values.companyName.trim()) {
    errors.companyName = "Company Name is required";
  }
  if (!values.mainContactName.trim()) {
    errors.mainContactName = "Primary Contact Name is required";
  }
  if (!values.mainContactEmail.trim()) {
    errors.mainContactEmail = "Contact Email is required";
  }
  if (
    values.companyWebsite.trim() &&
    !WEBSITE_REGEX.test(values.companyWebsite.trim())
  ) {
    errors.companyWebsite = WEB_ADDRESS_FORMAT_MESSAGE;
  }
  if (
    values.mainContactEmail.trim() &&
    !EMAIL_PATTERN.test(values.mainContactEmail.trim())
  ) {
    errors.mainContactEmail = "Please enter a valid email address.";
  }
  (["mainPhoneNumber", "mobilePhoneNumber"] as const).forEach((field) => {
    const phoneNumber = values[field].trim();
    const phoneDigitCount = (phoneNumber.match(/\d/g) || []).length;
    if (
      phoneNumber &&
      (!PHONE_ALLOWED_CHARACTERS_PATTERN.test(phoneNumber) ||
        phoneDigitCount < 7 ||
        phoneDigitCount > 15)
    ) {
      errors[field] =
        "Enter a phone number with 7 to 15 digits, for example (705) 555-0123.";
    }
  });
  if (
    values.mainPhoneNumberExtension.trim() &&
    !PHONE_EXTENSION_PATTERN.test(values.mainPhoneNumberExtension.trim())
  ) {
    errors.mainPhoneNumberExtension =
      "Enter the extension using numbers only, up to 8 digits.";
  }
  const fieldsWithMaximumLengths: Array<{
    field: keyof ProspectFormValues;
    label: string;
    maximumLength: number;
  }> = [
    {
      field: "leadCaptureId",
      label: "Lead Capture",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.leadCaptureId,
    },
    {
      field: "createdByName",
      label: "Created By",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.createdByName,
    },
    {
      field: "companyName",
      label: "Company Name",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.companyName,
    },
    {
      field: "companyWebsite",
      label: "Company Website",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.companyWebsite,
    },
    {
      field: "mainContactName",
      label: "Primary Contact Name",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainContactName,
    },
    {
      field: "mainContactTitle",
      label: "Title",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainContactTitle,
    },
    {
      field: "mainContactEmail",
      label: "Contact Email",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainContactEmail,
    },
    {
      field: "mainPhoneNumber",
      label: "Contact Number",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainPhoneNumber,
    },
    {
      field: "mainPhoneNumberExtension",
      label: "Ext.",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainPhoneNumberExtension,
    },
    {
      field: "mobilePhoneNumber",
      label: "Mobile Phone Number",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mobilePhoneNumber,
    },
    {
      field: "streetAddress",
      label: "Street Address",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.streetAddress,
    },
    {
      field: "city",
      label: "City",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.city,
    },
    {
      field: "province",
      label: "Province/State",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.province,
    },
    {
      field: "postalCode",
      label: "Postal/Zip Code",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.postalCode,
    },
    {
      field: "country",
      label: "Country",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.country,
    },
    {
      field: "pendingNoteComment",
      label: "Conversation Notes and Comments",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.pendingNoteComment,
    },
  ];

  fieldsWithMaximumLengths.forEach(({ field, label, maximumLength }) => {
    if (values[field].trim().length > maximumLength) {
      errors[field] = getProspectFieldLengthMessage(label, maximumLength);
    }
  });

  return errors;
};

const optionalTrimmedValue = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue || null;
};

export const toProspectFieldsInput = (
  values: ProspectFormValues,
): ProspectFieldsInput => ({
  leadCaptureId: values.leadCaptureId.trim(),
  createdByName: optionalTrimmedValue(values.createdByName),
  companyName: values.companyName.trim(),
  companyWebsite: optionalTrimmedValue(values.companyWebsite),
  mainContactName: values.mainContactName.trim(),
  mainContactTitle: optionalTrimmedValue(values.mainContactTitle),
  mainContactEmail: values.mainContactEmail.trim(),
  mainPhoneNumber: optionalTrimmedValue(values.mainPhoneNumber),
  mainPhoneNumberExtension: optionalTrimmedValue(
    values.mainPhoneNumberExtension,
  ),
  mobilePhoneNumber: optionalTrimmedValue(values.mobilePhoneNumber),
  address: {
    streetAddress: optionalTrimmedValue(values.streetAddress),
    city: optionalTrimmedValue(values.city),
    province: optionalTrimmedValue(values.province),
    postalCode: optionalTrimmedValue(values.postalCode),
    country: optionalTrimmedValue(values.country),
  },
});

export const mapProspectToFormValues = (
  prospect: ProspectRecordForForm,
): ProspectFormValues => ({
  leadCaptureId: prospect.leadCaptureId,
  createdByName: prospect.createdByName || "",
  companyName: prospect.companyName,
  companyWebsite: prospect.companyWebsite || "",
  mainContactName: prospect.mainContactName || "",
  mainContactTitle: prospect.mainContactTitle || "",
  mainContactEmail: prospect.mainContactEmail || "",
  mainPhoneNumber: prospect.mainPhoneNumber || "",
  mainPhoneNumberExtension: prospect.mainPhoneNumberExtension || "",
  mobilePhoneNumber: prospect.mobilePhoneNumber || "",
  streetAddress: prospect.address.streetAddress || "",
  city: prospect.address.city || "",
  province: prospect.address.province || "",
  postalCode: prospect.address.postalCode || "",
  country: prospect.address.country || "",
  pendingNoteComment: "",
});

export const prospectFormValuesMatch = (
  left: ProspectFormValues,
  right: ProspectFormValues,
): boolean =>
  (Object.keys(left) as Array<keyof ProspectFormValues>).every(
    (field) => left[field] === right[field],
  );

/**
 * Delegates to the shared plain-language helper: server text without the
 * Apollo wrapper, a network message, a UserFacingError, or the fallback.
 */
export const getProspectErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => getUserFacingErrorText(error, fallbackMessage);
